import React, { ReactNode } from 'react';

interface PageContainerProps {
  title: string;
  description?: string;
  icon?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}

/**
 * PageContainer Component
 * 
 * Provides a consistent layout wrapper for all pages in the application
 * Includes a header with title, description, and optional icon and actions
 * 
 * @param title - The page title
 * @param description - Optional page description
 * @param icon - Optional icon to display next to the title
 * @param children - Page content
 * @param actions - Optional action buttons to display in the header
 */
export const PageContainer: React.FC<PageContainerProps> = ({
  title,
  description,
  icon,
  children,
  actions,
}) => {
  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pt-16 pb-10">
      <header className="mb-8">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            {icon && (
              <div className="mr-4 p-2.5 rounded-full bg-[var(--background-surface)] text-[var(--brand-primary)]">
                {icon}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[var(--text-primary)]">{title}</h1>
              {description && (
                <p className="text-[var(--text-secondary)] mt-1.5 text-sm sm:text-base">{description}</p>
              )}
            </div>
          </div>
          
          {actions && (
            <div className="flex space-x-3">
              {actions}
            </div>
          )}
        </div>
      </header>
      
      <main className="mb-6">
        {children}
      </main>
    </div>
  );
};

export default PageContainer; 